// src/components/About.jsx

import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

import bgVideo from "../assets/vision-video.mp4";
import bgPoster from "../assets/vision-video-poster.png";
import VideoBackground from "../layout/VideoBackground";
import block1Img from "../assets/block1.jpg";
import block2Img from "../assets/block.jpg";
import block3Img from "../assets/block3.jpeg";


const Section = ({ children }) => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 92%", "end 55%"],
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [0.2, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [40, 0]);
  const blur = useTransform(scrollYProgress, [0, 0.35], [8, 0]);
  const filter = useTransform(blur, (v) => `blur(${v}px)`);

  return (
    <motion.div
      ref={ref}
      style={{ opacity, y, filter }}
      className="will-change-transform"
    >
      {children}
    </motion.div>
  );
};

// Bloque de texto sin imagen — usado para block1 / block2 / block3
const TextBlock = ({ title, paragraphs, showTitle = true }) => (
  <Section>
    <div className="flex flex-col gap-4 max-w-xl mx-auto">
      {showTitle && (
        <h2
          className="
            uppercase
            text-[#C6A98A]
            font-light

            text-xl
            tracking-[0.18em]

            sm:text-2xl
            sm:tracking-[0.22em]

            md:text-3xl

            text-center
          "
        >
          {title}
        </h2>
      )}

      {paragraphs.map((p, i) => (
        <p
          key={i}
          className="
            text-[#E8D8C3]
            font-light
            leading-relaxed
            whitespace-pre-line

            text-sm
            sm:text-base
            md:text-lg

            text-center
          "
        >
          {p}
        </p>
      ))}
    </div>
  </Section>
);

// Imagen de la fila: título centrado, click abre modal con paragraphs
const ImageRowItem = ({ title, image, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group relative w-full h-[340px] sm:h-[420px] md:h-[520px] overflow-hidden block text-left"
  >
    <img
      src={image}
      alt={title}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
    />

    {/* overlay para legibilidad del título */}
    <div className="absolute inset-0 bg-[#0E0B09]/40 group-hover:bg-[#0E0B09]/55 transition-colors duration-300 pointer-events-none" />

    <span
      className="
        absolute inset-0

        flex items-center justify-center
        text-center
        px-6

        uppercase
        text-[#E8D8C3]
        font-light

        text-base
        sm:text-lg
        md:text-xl

        tracking-[0.15em]

        group-hover:text-[#C6A98A]
        transition-colors
        duration-300
      "
    >
      {title}
    </span>
  </button>
);

// Modal que muestra los paragraphs del block seleccionado
const BlockModal = ({ block, onClose }) => {
  if (!block) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-[#0E0B09]/85 backdrop-blur-sm flex items-center justify-center px-6 py-16"
      >
        <motion.div
          key="modal"
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="
            relative
            w-full
            max-w-xl
            max-h-[80vh]
            overflow-y-auto

            bg-[#14100C]
            border border-[#3C2B22]/60

            px-6 py-10
            sm:px-10 sm:py-12
          "
        >
          {/* CERRAR */}
          <button
            type="button"
            onClick={onClose}
            className="
              absolute top-4 right-4 sm:top-6 sm:right-6

              text-[#8C6F5A]
              hover:text-[#C6A98A]

              uppercase
              tracking-[0.2em]
              text-[10px]

              transition-colors
              duration-300
            "
          >
             ✕
          </button>

          <h3
            className="
              uppercase
              text-[#C6A98A]
              font-light

              text-xl
              sm:text-2xl

              tracking-[0.15em]

              mb-8
              pr-16
            "
          >
            {block.title}
          </h3>

          <div className="flex flex-col gap-4">
            {block.paragraphs.map((p, i) => (
              <p
                key={i}
                className="
                  text-[#E8D8C3]
                  font-light
                  leading-relaxed
                  whitespace-pre-line

                  text-sm
                  sm:text-base
                "
              >
                {p}
              </p>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const About = () => {
  const { t } = useTranslation();
  const [activeBlock, setActiveBlock] = useState(null);

  const blocks = [
    { key: "block1", showTitle: false }, // el título ya se usó en la banda
    { key: "block2", showTitle: true },
    { key: "block3", showTitle: true },
  ];

  const imageRowItems = [
    { key: "block1", image: block1Img },
    { key: "block2", image: block2Img },
    { key: "block3", image: block3Img },
  ];

  const openModal = (key) => {
    setActiveBlock({
      title: t(`about.${key}.title`),
      paragraphs: t(`about.${key}.paragraphs`, { returnObjects: true }),
    });
  };

  const closeModal = () => setActiveBlock(null);

  return (
    <div className="relative w-full overflow-hidden">
      {/* BACKGROUND */}
      <VideoBackground src={bgVideo} poster={bgPoster} fixed overlayClass="bg-[#0E0B09]/80" />

      {/* ============ CONTENIDO SUPERIOR (con max-width) ============ */}
      <div
        className="
          relative z-10
          w-full
          max-w-[92%]
          sm:max-w-xl
          md:max-w-3xl
          lg:max-w-5xl
          mx-auto
          flex flex-col
          pt-32 sm:pt-36 md:pt-28
        "
      >
        {/* HEADER */}
        <Section>
          <div className="max-w-xl mx-auto text-center flex flex-col gap-6 mb-10 md:mb-6">
            <h1
              className="
                uppercase
                text-[#C6A98A]
                font-light

                text-2xl
                tracking-[0.18em]

                sm:text-3xl
                sm:tracking-[0.22em]

                md:text-5xl
                md:tracking-[0.3em]
              "
            >
              {t("about.title")}
            </h1>

            <div className="w-16 h-px mx-auto bg-[#8C6F5A]/40" />
          </div>
        </Section>

        {/* CONOCÉ A ALDANA */}
        <Section>
          <div className="max-w-2xl mx-auto">
            {/* TEXTO */}
            <div className="flex flex-col gap-5 items-center text-center">
              <p className="uppercase text-[#8C6F5A] tracking-[0.35em] text-[10px] sm:text-xs font-light">
                {t("about.meet.eyebrow")}
              </p>

              <h2
                className="
                  uppercase
                  text-[#C6A98A]
                  font-light

                  text-2xl
                  tracking-[0.1em]

                  sm:text-3xl

                  md:text-4xl
                "
              >
                {t("about.meet.title")}
              </h2>

              <p className="text-[#E8D8C3] font-light leading-relaxed text-sm sm:text-base md:text-lg">
                {t("about.meet.text")}
              </p>

              <div>
                <span
                  className="
                    inline-block
                    border border-[#8C6F5A]/40

                    uppercase
                    tracking-[0.22em]
                    text-[10px] sm:text-[11px]

                    text-[#C6A98A]
                    font-light

                    px-6 py-2.5

                    hover:border-[#C6A98A]/60
                    hover:text-[#E8D8C3]
                    transition-colors
                    duration-300
                    cursor-pointer
                  "
                >
                  {t("about.meet.cta")}
                </span>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* ============ BANDA FULL-WIDTH ============ */}
      <div className="relative z-10 w-full bg-[#14100C]/80 border-y border-[#3C2B22]/40 mt-24 md:mt-32">
        <Section>
          <div className="max-w-3xl mx-auto text-center py-14 md:py-20 px-6">
            <p
              className="
                uppercase
                text-[#C6A98A]
                font-light

                text-lg
                tracking-[0.15em]

                sm:text-xl
                sm:tracking-[0.2em]

                md:text-2xl
                md:tracking-[0.25em]
              "
            >
              {t("about.block1.title")}
            </p>
          </div>
        </Section>
      </div>

      {/* ============ FILA DE 3 IMÁGENES FULL-BLEED ============ */}
      <div className="relative z-10 w-full grid grid-cols-1 sm:grid-cols-3">
        {imageRowItems.map(({ key, image }) => (
          <ImageRowItem
            key={key}
            title={t(`about.${key}.title`)}
            image={image}
            onClick={() => openModal(key)}
          />
        ))}
      </div>

      {/* ============ CONTENIDO INFERIOR (con max-width) ============ */}
      <div
        className="
          relative z-10
          w-full
          max-w-[92%]
          sm:max-w-xl
          md:max-w-3xl
          lg:max-w-5xl
          mx-auto
          flex flex-col
          pb-24
        "
      >
        <div className="flex flex-col gap-20 md:gap-28 mt-20 md:mt-28">
          {blocks.map(({ key, showTitle }) => (
            <TextBlock
              key={key}
              title={t(`about.${key}.title`)}
              paragraphs={t(`about.${key}.paragraphs`, { returnObjects: true })}
              showTitle={showTitle}
            />
          ))}
        </div>

        {/* CIERRE */}
        <Section>
          <div className="mt-20 md:mt-28 text-center max-w-md mx-auto flex flex-col gap-3">
            <p
              className="
                text-[#C6A98A]
                uppercase
                tracking-[0.3em]

                text-[10px]
                sm:text-xs
              "
            >
              {t("about.keywords")}
            </p>

            <div className="w-20 h-px mx-auto bg-[#C6A98A]/40 mt-3" />
          </div>
        </Section>
      </div>


      {/* MODAL */}
      {activeBlock && <BlockModal block={activeBlock} onClose={closeModal} />}
    </div>
  );
};

export default About;